import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircle2, FileText, XCircle, CreditCard, ShieldAlert, Clock, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { api, fmtUSD } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { SecurityBanner } from '@/components/SecurityBanner';

const STATUS_META = {
  draft: { label: 'Brouillon', color: 'bg-slate-100 text-slate-600', icon: FileText },
  sent: { label: 'Envoyé au client', color: 'bg-[#EAF3FF] text-[#0E3F80]', icon: Clock },
  accepted: { label: 'Accepté — en attente de paiement', color: 'bg-[#FFF6C7] text-[#7A5F00]', icon: Clock },
  rejected: { label: 'Refusé', color: 'bg-[#FFE1E4] text-[#A10B18]', icon: XCircle },
  paid_escrow: { label: 'Payé — fonds en séquestre', color: 'bg-[#EAF3FF] text-[#0E3F80]', icon: ShieldAlert },
  completed_pending: { label: 'Terminé — en attente de validation', color: 'bg-[#FFF6C7] text-[#7A5F00]', icon: Clock },
  released: { label: 'Libéré au jobeur', color: 'bg-[#DFF7EA] text-[#16764A]', icon: CheckCircle2 },
  disputed: { label: 'Litige en cours', color: 'bg-[#FFE1E4] text-[#A10B18]', icon: AlertTriangle },
  refunded: { label: 'Remboursé', color: 'bg-slate-100 text-slate-600', icon: XCircle },
};

export default function QuoteDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [q, setQ] = useState(null);
  const [payment, setPayment] = useState(null);
  const [disputeReason, setDisputeReason] = useState('');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const r = await api.get(`/quotes/${id}`);
    setQ(r.data);
    if (r.data.payment_id) {
      try { const pr = await api.get(`/payments/${r.data.payment_id}`); setPayment(pr.data); } catch {}
    }
    setLoading(false);
  };
  useEffect(() => { load(); // eslint-disable-next-line
  }, [id]);

  const isClient = user?.id === q?.client_id;
  const isJobeur = user?.id === q?.jobeur_id;
  const meta = STATUS_META[q?.status || 'draft'];

  const accept = async () => { try { await api.post(`/quotes/${id}/accept`); toast.success('Devis accepté'); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); } };
  const reject = async () => { try { await api.post(`/quotes/${id}/reject`); toast.success('Devis refusé'); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); } };
  const validate = async () => { try { await api.post(`/payments/${payment.id}/validate`); toast.success('Paiement validé et libéré'); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); } };
  const markComplete = async () => { try { await api.post(`/payments/${payment.id}/mark-completed`); toast.success('Service marqué comme terminé'); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); } };
  const dispute = async () => { try { await api.post(`/payments/${payment.id}/dispute?reason=${encodeURIComponent(disputeReason)}`); toast.success('Litige ouvert'); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); } };

  if (loading) return <div className="p-6">Chargement…</div>;
  if (!q) return <div className="p-6">Devis introuvable</div>;
  const b = q.breakdown;

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-6 space-y-4" data-testid="quote-detail-page">
      <div className="flex items-center gap-2">
        <FileText size={22} className="text-[#1677F2]" />
        <h1 className="text-2xl font-bold">{q.title}</h1>
      </div>
      <span className={`inline-flex items-center gap-1 ${meta.color} rounded-full px-3 py-1 text-sm font-semibold`} data-testid="quote-status-badge">
        <meta.icon size={14} /> {meta.label}
      </span>
      <SecurityBanner variant="compact" />

      <Card className="p-4">
        {q.description && <p className="text-sm text-slate-700 mb-3 whitespace-pre-wrap">{q.description}</p>}

        <h3 className="font-semibold mb-2">Main d&apos;œuvre</h3>
        <RowLine label="Prestation" value={fmtUSD(b.labor_cents)} />

        {q.expenses?.length > 0 && (
          <>
            <h3 className="font-semibold mt-4 mb-2">Frais annexes</h3>
            {q.expenses.map((e, i) => (
              <div key={i} className="flex items-center justify-between text-sm py-1">
                <span className="flex items-center gap-2">
                  {e.receipt_base64 && <img src={e.receipt_base64} alt="" className="h-8 w-8 rounded object-cover" />}
                  {e.description}
                </span>
                <span className="font-mono-num font-semibold">{fmtUSD(e.amount_cents)}</span>
              </div>
            ))}
            <RowLine label="Sous-total FA" value={fmtUSD(b.expenses_cents)} muted />
          </>
        )}

        <Separator className="my-3" />
        <h3 className="font-semibold mb-2">Commission Job Kongo</h3>
        <RowLine label="10% (MO)" value={fmtUSD(b.fee_mo_total)} muted />
        <RowLine label="3% assurance (FA)" value={fmtUSD(b.fee_fa_insurance)} muted />

        <Separator className="my-3" />
        <div className="space-y-1">
          <RowLine label="👉 Total à payer par le client" value={fmtUSD(b.client_total)} strong testid="quote-total-client" />
          <RowLine label="Reçu par le jobeur" value={fmtUSD(b.jobeur_payout)} />
          <RowLine label="Perçu par Job Kongo" value={fmtUSD(b.platform_revenue)} />
        </div>
      </Card>

      {/* Actions */}
      {q.status === 'sent' && isClient && (
        <Card className="p-4 flex gap-2">
          <Button onClick={reject} variant="outline" className="flex-1" data-testid="quote-reject-btn">Refuser</Button>
          <Button onClick={accept} className="flex-1 bg-[#1677F2] hover:bg-[#0F5FCC]" data-testid="quote-accept-btn">Accepter le devis</Button>
        </Card>
      )}

      {q.status === 'accepted' && isClient && (
        <Card className="p-4">
          <p className="text-sm mb-3">Validez pour payer en toute sécurité. Les fonds restent en séquestre jusqu&apos;à la fin de la prestation.</p>
          <Button onClick={() => nav(`/payment/${q.id}`)} className="w-full bg-[#1677F2] hover:bg-[#0F5FCC]" data-testid="quote-pay-btn">
            <CreditCard size={16} className="mr-1" /> Payer {fmtUSD(b.client_total)}
          </Button>
        </Card>
      )}

      {q.status === 'paid_escrow' && payment && isJobeur && (
        <Card className="p-4">
          <Button onClick={markComplete} className="w-full bg-[#1F9D63] hover:bg-[#16764A]" data-testid="payment-mark-complete-btn">
            <CheckCircle2 size={16} className="mr-1" /> J&apos;ai terminé le service
          </Button>
        </Card>
      )}

      {q.status === 'completed_pending' && payment && (
        <Card className="p-4 space-y-2">
          <p className="text-sm">Service terminé. Validez pour libérer les fonds, ou ouvrez un litige si un problème.</p>
          {isClient && (
            <div className="flex gap-2">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="flex-1 text-[#CE1021] border-[#FFD7DB]" data-testid="payment-dispute-btn">Ouvrir un litige</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Ouvrir un litige</AlertDialogTitle>
                    <AlertDialogDescription>Décrivez le problème. L&apos;équipe Job Kongo médiera sous 48h.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <Textarea placeholder="Raison du litige…" value={disputeReason} onChange={(e) => setDisputeReason(e.target.value)} data-testid="dispute-reason" />
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                    <AlertDialogAction onClick={dispute} className="bg-[#CE1021] hover:bg-[#A10B18]">Confirmer</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <Button onClick={validate} className="flex-1 bg-[#1F9D63] hover:bg-[#16764A]" data-testid="payment-validate-btn">
                Valider & libérer
              </Button>
            </div>
          )}
        </Card>
      )}

      {q.status === 'released' && (
        <Card className="p-4 bg-[#DFF7EA] border-[#1F9D63]/30">
          <p className="text-sm text-[#16764A] font-medium flex items-center gap-2"><CheckCircle2 size={18} /> Fonds libérés. Laissez un avis !</p>
          <Button onClick={() => nav(`/review/${payment?.id}`)} className="mt-2 bg-[#1F9D63] hover:bg-[#16764A]" data-testid="review-submit-nav">Laisser un avis</Button>
        </Card>
      )}

      {q.status === 'disputed' && (
        <Card className="p-4 bg-[#FFE1E4] border-[#CE1021]/30">
          <p className="text-sm text-[#A10B18] font-medium">Litige en cours — l&apos;équipe Job Kongo traite votre dossier.</p>
          {payment?.dispute_reason && <p className="text-xs mt-1 text-slate-700">Raison: {payment.dispute_reason}</p>}
        </Card>
      )}
    </div>
  );
}

const RowLine = ({ label, value, muted, strong, testid }) => (
  <div className={`flex items-center justify-between ${strong ? 'text-base' : 'text-sm'} py-0.5`}>
    <span className={muted ? 'text-slate-600' : 'text-slate-700'}>{label}</span>
    <span className={`font-mono-num ${strong ? 'font-bold text-[#0B1220]' : 'font-semibold text-[#0B1220]'}`} data-testid={testid}>{value}</span>
  </div>
);
